<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Cluster } from '@/services/types/api.types';
import InlineEditor from '@/components/InlineEditor.vue';

defineProps<{
  cluster: Cluster;
  dragOverElement: string | null;
  isDragDisabled: boolean;
}>();

const emit = defineEmits<{
  (e: 'save'): void;
  (e: 'update-theme', cluster: Cluster, newValue: string): void;
  (e: 'update-idea', cluster: Cluster, idea: any, newValue: string): void;
  (e: 'delete-theme', cluster: Cluster): void;
  (e: 'delete-idea', cluster: Cluster, idea: any): void;
  (e: 'start-edit', item: any, type: 'theme' | 'idea'): void;
  (e: 'finish-edit', item: any, type: 'theme' | 'idea'): void;
  (e: 'dragstart', event: DragEvent, item: any, type: 'theme' | 'idea', sourceTheme?: string): void;
  (e: 'dragover', event: DragEvent): void;
  (e: 'dragenter', event: DragEvent, item: any, type: 'theme' | 'idea'): void;
  (e: 'dragleave', event: DragEvent): void;
  (e: 'drop', event: DragEvent, item: any, type: 'theme' | 'idea'): void;
  (e: 'move-idea', cluster: Cluster, idea: any): void;
  (e: 'merge-theme', cluster: Cluster): void;
  (e: 'merge-idea', cluster: Cluster, idea: any): void;
  (e: 'view-contributions', cluster: Cluster, idea: any): void;
}>();

const { t } = useI18n();
const isCollapsed = ref(false);

function toggleCollapsed() {
  isCollapsed.value = !isCollapsed.value;
}
</script>

<template>
  <q-card flat bordered class="theme-card q-mb-md">
    <!-- Theme Header (Drop zone for ideas) -->
    <q-card-section
      :class="[
        'theme-header',
        {
          'drag-over': dragOverElement === `theme-${cluster.theme}`,
          'drag-disabled': isDragDisabled,
        },
      ]"
      :draggable="!isDragDisabled"
      @dragstart="emit('dragstart', $event, cluster, 'theme')"
      @dragover="emit('dragover', $event)"
      @dragenter="emit('dragenter', $event, cluster, 'theme')"
      @dragleave="emit('dragleave', $event)"
      @drop="emit('drop', $event, cluster, 'theme')"
    >
      <inline-editor
        :model-value="cluster.theme"
        @update:model-value="(val) => emit('update-theme', cluster, val)"
        @save="emit('save')"
        show-delete
        @delete="emit('delete-theme', cluster)"
        @startEdit="emit('start-edit', cluster, 'theme')"
        @finishEdit="emit('finish-edit', cluster, 'theme')"
      >
        <template #actions>
          <q-btn
            dense
            unelevated
            size="sm"
            :icon="isCollapsed ? 'expand_more' : 'expand_less'"
            :disable="isDragDisabled"
            @mousedown.stop
            @click.stop="toggleCollapsed()"
          >
            <q-tooltip>
              {{ isCollapsed ? t('consultation.ai.themes.expand') : t('consultation.ai.themes.collapse') }}
            </q-tooltip>
          </q-btn>
          <q-btn
            dense
            unelevated
            size="sm"
            icon="call_merge"
            :disable="isDragDisabled"
            @click.stop="emit('merge-theme', cluster)"
          >
            <q-tooltip>{{ t('consultation.ai.mergeThemes.action') }}</q-tooltip>
          </q-btn>
        </template>
        <template #default="{ value }">
          <div class="theme-content">
            <q-icon name="drag_indicator" class="drag-handle q-mr-sm" />
            <span class="text-h6">{{ value }}</span>
            <q-badge :label="cluster.ideas.length" color="primary" class="q-ml-sm" />
          </div>
        </template>
      </inline-editor>
    </q-card-section>

    <!-- Ideas List -->
    <q-slide-transition>
      <div v-show="!isCollapsed">
        <q-card-section>
          <q-list v-if="cluster.ideas.length > 0" dense>
            <q-item
              v-for="(idea, ideaIdx) in cluster.ideas"
              :key="ideaIdx"
              :class="[
                'draggable-idea q-pa-sm',
                {
                  'drag-over': dragOverElement === `idea-${idea.representative_idea}`,
                  'drag-disabled': isDragDisabled,
                },
              ]"
              :draggable="!isDragDisabled"
              @dragstart="emit('dragstart', $event, idea, 'idea', cluster.theme)"
              @dragover="emit('dragover', $event)"
              @dragenter="emit('dragenter', $event, idea, 'idea')"
              @dragleave="emit('dragleave', $event)"
              @drop="emit('drop', $event, idea, 'idea')"
            >
              <q-item-section>
                <inline-editor
                  :model-value="idea.representative_idea"
                  @update:model-value="(val) => emit('update-idea', cluster, idea, val)"
                  @save="emit('save')"
                  show-delete
                  @delete="emit('delete-idea', cluster, idea)"
                  @startEdit="emit('start-edit', idea, 'idea')"
                  @finishEdit="emit('finish-edit', idea, 'idea')"
                >
                  <template #actions>
                    <q-btn
                      dense
                      unelevated
                      size="sm"
                      icon="drive_file_move"
                      :disable="isDragDisabled"
                      @click.stop="emit('move-idea', cluster, idea)"
                    >
                      <q-tooltip>{{ t('consultation.ai.moveIdea.action') }}</q-tooltip>
                    </q-btn>
                    <q-btn
                      dense
                      unelevated
                      size="sm"
                      icon="call_merge"
                      :disable="isDragDisabled"
                      @click.stop="emit('merge-idea', cluster, idea)"
                    >
                      <q-tooltip>{{ t('consultation.ai.mergeIdeas.action') }}</q-tooltip>
                    </q-btn>
                    <q-btn
                      dense
                      unelevated
                      size="sm"
                      icon="article"
                      :disable="isDragDisabled"
                      @click.stop="emit('view-contributions', cluster, idea)"
                    >
                      <q-tooltip>{{ t('consultation.ai.contributions.view') }}</q-tooltip>
                    </q-btn>
                  </template>
                  <template #default="{ value }">
                    <div class="idea-content">
                      <q-icon name="drag_indicator" class="drag-handle q-mr-sm" size="sm" />
                      <span>{{ value }}</span>
                      <q-badge
                        :label="`${idea.answer_ids.length}x`"
                        color="blue-grey-5"
                        text-color="white"
                        class="q-ml-sm"
                      />
                    </div>
                  </template>
                </inline-editor>
              </q-item-section>
            </q-item>
          </q-list>
          <div v-else class="text-caption text-grey-6 q-pa-sm">
            {{ t('consultation.ai.themes.noIdeas') }}
          </div>
        </q-card-section>
      </div>
    </q-slide-transition>
  </q-card>
</template>

<style scoped>
.theme-card {
  transition: all 0.2s ease;
}

.theme-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.theme-header {
  cursor: grab;
  border-bottom: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  padding: 16px;
}

.theme-header:active {
  cursor: grabbing;
}

.theme-header.drag-over {
  background-color: #e8f5e8 !important;
  border: 2px solid #4caf50;
}

.theme-header.drag-disabled {
  cursor: default !important;
}

.theme-content,
.idea-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.drag-handle {
  color: #999;
  opacity: 0.6;
  cursor: grab;
  flex-shrink: 0;
}

.drag-handle:hover {
  opacity: 1;
  color: #2196f3;
}

.theme-header.drag-disabled .drag-handle,
.draggable-idea.drag-disabled .drag-handle {
  cursor: not-allowed !important;
  opacity: 0.3 !important;
  color: #ccc !important;
}

.draggable-idea {
  cursor: grab;
  border: 2px solid transparent;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.draggable-idea:hover {
  border-color: #e3f2fd;
  background-color: #f8f9fa;
}

.draggable-idea:active {
  cursor: grabbing;
  opacity: 0.7;
}

.draggable-idea.drag-over {
  border-color: #4caf50 !important;
  background-color: #e8f5e8 !important;
  box-shadow: 0 0 16px rgba(76, 175, 80, 0.4);
  transform: scale(1.02);
}

.draggable-idea.drag-disabled {
  cursor: default !important;
}

.draggable-idea.drag-disabled:hover {
  border-color: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  transform: none !important;
}
</style>
